import React from "react";
import { MapPin, Loader2, RefreshCw, Settings } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * LocationPrompt — modal that explains why GPS failed and offers a retry.
 *
 * Behaviour:
 *  - Detects whether we're in a TWA (Play Store install) vs regular browser
 *    and tailors the OS-specific instructions accordingly. TWA users hit a
 *    different failure path (Chrome's per-origin permission for taladraw.com
 *    is stuck "denied", inherited from a prior browser-side rejection — and
 *    flipping Android system Location ON doesn't fix it).
 *  - Reads `navigator.permissions.query({name:'geolocation'})` to render a
 *    live diagnostic showing actual state of every permission layer so the
 *    user can see WHICH layer is blocking them.
 *
 * Props:
 *  - open              : boolean — controlled visibility
 *  - onOpenChange      : (bool) — controlled close
 *  - errorCode         : 1 (denied) | 2 (unavailable) | 3 (timeout) | "unsupported"
 *  - retrying          : boolean — disables Try-Again while a fix is in flight
 *  - onRetry           : () => void — re-invoke the geolocation request
 */

function detectEnv() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const platform = (typeof navigator !== "undefined" && navigator.platform) || "";
  const isStandalone =
    (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    (typeof document !== "undefined" && document.referrer && document.referrer.startsWith("android-app://")) ||
    (typeof window !== "undefined" && window.navigator && window.navigator.standalone === true);
  let os = "desktop";
  if (/iPad|iPhone|iPod/.test(ua) || (platform === "MacIntel" && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1)) {
    os = "ios";
  } else if (/Android/i.test(ua)) {
    os = "android";
  }
  return { os, isStandalone };
}

const STEPS = {
  ios_browser: [
    "Open the Settings app on your iPhone.",
    "Tap Privacy & Security → Location Services.",
    "Make sure Location Services is ON.",
    "Scroll down to Safari Websites and set it to While Using the App.",
    "Return to TalaDraw and tap Try Again below.",
  ],
  ios_standalone: [
    "Open Settings → TalaDraw → Location → set to While Using.",
    "If Location is already on, the issue may be a stale Safari permission. Open Safari → tap aA → Website Settings → reset Location for taladraw.com.",
    "Return to TalaDraw and tap Try Again.",
  ],
  android_browser: [
    "Open your phone's Settings.",
    "Tap Location → make sure Location is ON.",
    "In Chrome, tap the lock icon next to the URL → Permissions → set Location to Allow.",
    "Return to TalaDraw and tap Try Again.",
  ],
  android_standalone: [
    "Open Settings → Apps → TalaDraw → Permissions → Location → set to Allow only while using.",
    "If that's already on and the prompt keeps appearing, the website itself is blocked in Chrome's per-origin store: open the regular Chrome browser app → go to taladraw.com → tap the lock icon → Permissions → reset Location.",
    "Force-close TalaDraw (swipe from recent apps) and reopen it.",
    "Tap Try Again below.",
  ],
  desktop: [
    "Click the lock or location icon in your browser's address bar.",
    "Set Location to Allow for this site.",
    "Make sure your computer's location services are enabled in OS Settings.",
    "Reload the page if the permission was just changed.",
    "Click Try Again below.",
  ],
};

const TITLES = {
  1: "Location permission needed",
  2: "Location is turned off",
  3: "Couldn't get a location fix",
  unsupported: "Location not supported",
};

const DESCRIPTIONS = {
  1: "TalaDraw needs your location to find ATMs near you. You've previously declined this permission — please re-enable it in your settings.",
  2: "Your device's GPS or location services appear to be off. Turn them on, then tap Try Again.",
  3: "We waited but couldn't pin down your location. Step outside or near a window, then try again.",
  unsupported: "This browser doesn't support geolocation. Try a recent version of Chrome, Safari, Firefox, or Edge.",
};

function StateBadge() { return null; }
// Intentionally a no-op — StateBadge was the visual building block for the
// diagnostic strip, which has been removed from this modal. Kept as a stub
// rather than deleted so other components that may import it don't break.

export default function LocationPrompt({ open, onOpenChange, errorCode, retrying, onRetry }) {
  const { os, isStandalone } = detectEnv();

  const stepKey =
    os === "ios" ? (isStandalone ? "ios_standalone" : "ios_browser") :
    os === "android" ? (isStandalone ? "android_standalone" : "android_browser") :
    "desktop";
  const steps = STEPS[stepKey];
  const title = TITLES[errorCode] || "Location unavailable";
  const description = DESCRIPTIONS[errorCode] || "We couldn't access your location. Please check your device settings and try again.";

  const envLabel =
    os === "ios" ? (isStandalone ? "iOS (installed app)" : "iOS Safari") :
    os === "android" ? (isStandalone ? "Android (installed app)" : "Android Chrome") :
    "Desktop";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-zinc-950 border-zinc-800 text-white"
        data-testid="location-prompt-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-9 h-9 rounded-full bg-[#00FF94]/15 border border-[#00FF94]/30 flex items-center justify-center">
              <MapPin className="w-4 h-4 text-[#00FF94]" strokeWidth={2} />
            </div>
            <DialogTitle className="text-base">{title}</DialogTitle>
          </div>
          <DialogDescription className="text-zinc-400 text-sm leading-relaxed">
            {description}
          </DialogDescription>
        </DialogHeader>

        {/* Diagnostic strip removed per user request. The strip lives on
            in NotificationPermissionBanner for the notification-permission
            debugging flow, but for location we keep the modal clean. */}

        {errorCode !== "unsupported" && (
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">
              <Settings className="w-3 h-3" strokeWidth={2} />
              How to fix ({envLabel})
            </div>
            <ol className="space-y-1.5 text-xs text-zinc-300">
              {steps.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#00FF94] font-bold shrink-0">{i + 1}.</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
            data-testid="location-prompt-cancel"
          >
            Cancel
          </Button>
          {errorCode !== "unsupported" && (
            <Button
              onClick={onRetry}
              disabled={retrying}
              className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-semibold"
              data-testid="location-prompt-retry"
            >
              {retrying ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Locating…</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-1.5" />Try Again</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
